class BuildABoxApp extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.strings = {};
    try {
      this.strings = JSON.parse(this.querySelector("[data-strings]").textContent);
    } catch (e) {}

    this.currency = this.dataset.currency || "USD";
    this.messageMax = Number(this.dataset.messageMax) || 180;
    this.cardRequired = this.dataset.cardRequired === "true";
    
    this.panels = {};
    this.querySelectorAll("[data-panel]").forEach((panel) => {
      this.panels[panel.dataset.panel] = panel;
    });
    // The card step only exists when the merchant added a card collection block.
    this.steps = ["box", "products", "card", "message", "review"].filter((name) => this.panels[name]);

    this.progressSteps = Array.from(this.querySelectorAll("[data-progress-step]"));
    this.progressFill = this.querySelector("[data-progress-fill]");
    this.progressMarker = this.querySelector("[data-progress-marker]");
    this.totalEl = this.querySelector("[data-total]");
    this.prevButton = this.querySelector("[data-prev]");
    this.nextButton = this.querySelector("[data-next]");
    this.tilesEl = this.querySelector("[data-tiles]");
    this.boxEmptyEl = this.querySelector("[data-box-empty]");
    this.capacityEl = this.querySelector("[data-capacity]");
    this.selectedBoxEl = this.querySelector("[data-selected-box]");
    this.selectedBoxImage = this.querySelector("[data-selected-box-image]");
    this.selectedBoxName = this.querySelector("[data-selected-box-name]");
    this.errorEls = Array.from(this.querySelectorAll("[data-error]"));
    this.reviewBox = this.querySelector("[data-review-box]");
    this.reviewItems = this.querySelector("[data-review-items]");
    this.reviewCard = this.querySelector("[data-review-card]");
    this.reviewNote = this.querySelector("[data-review-note]");
    this.reviewTotal = this.querySelector("[data-review-total]");
    this.messageInput = this.querySelector("[data-message]");
    this.fromInput = this.querySelector("[data-from]");
    this.skipInput = this.querySelector("[data-skip-message]");
    this.messageFields = this.querySelector("[data-message-fields]");
    this.counterEl = this.querySelector("[data-message-counter]");
    this.toastEl = this.querySelector("[data-toast]");
    this.aside = this.querySelector("[data-aside]");
    this.sheetToggle = this.querySelector("[data-sheet-toggle]");
    this.sheetOverlay = this.querySelector("[data-sheet-overlay]");
    this.sheetTotalEl = this.querySelector("[data-sheet-total]");
    this.toastTimer = null;
    this.uid = 0;
    // Units of each variant already in the customer's cart (from /cart.js). Shopify
    // rejects the whole add-to-cart with "The product ... is already sold out" once
    // cart + request exceed stock, so stock shown here must subtract what's in the cart.
    this.cartQty = {};

    this.addEventListener("click", (event) => this.handleClick(event));
    this.addEventListener("change", (event) => this.handleChange(event));
    this.addEventListener("input", (event) => this.handleInput(event));
    // On mobile the right-hand box panel is a bottom sheet: the toggle (or the dimmed
    // backdrop) slides it up/down. Desktop hides the toggle and shows the panel inline.
    this.sheetToggle.addEventListener("click", () => this.setSheet(!this.aside.classList.contains("is-open")));
    this.sheetOverlay.addEventListener("click", () => this.setSheet(false));
    this.addEventListener("keydown", (event) => {
      if (event.key === "Escape") this.setSheet(false);
    });
    this.prevButton.addEventListener("click", () => this.go(-1));
    this.nextButton.addEventListener("click", () => this.onNext());

    // bfcache restores the JS heap exactly as it was when the customer left the page.
    // If the box was submitted (saved state cleared on success) Back must start over;
    // if they simply navigated away mid-build, keep what they had.
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        if (!this.hasSaved()) {
          this.reset();
          this.refreshCartChrome();
        }
        this.refreshCartQty();
      }
    });

    // Restore an in-progress build (e.g. after a page refresh) before the first render.
    this.reset({ save: false });
    this.restoreSaved();
    this.render();
    this.refreshCartQty();
    this.subscribeToCartUpdated(50);
  }

  subscribeToCartUpdated(attemptsLeft) {
    // window.on is assigned by theme.js during its own init and may not exist yet.
    if (typeof window.on === "function") {
      window.on("cart:updated", () => this.refreshCartQty());
      return;
    }
    if (attemptsLeft <= 0) return;
    setTimeout(() => this.subscribeToCartUpdated(attemptsLeft - 1), 100);
  }

  refreshCartQty() {
    return fetch("/cart.js", { credentials: "include" })
      .then((response) => response.json())
      .then((cart) => {
        const qty = {};
        (cart.items || []).forEach((line) => {
          qty[String(line.variant_id)] = (qty[String(line.variant_id)] || 0) + line.quantity;
        });
        this.cartQty = qty;
        this.render();
      })
      .catch(() => {});
  }

  // How many more units of a variant can go in this box: stock minus what's already in
  // the cart. Untracked / "continue selling" variants are effectively unlimited.
  remaining(variantId, max, tracked) {
    if (!tracked) return Infinity;
    return max - (this.cartQty[String(variantId)] || 0);
  }

  setSheet(open) {
    // With the drawer open, keep the page behind it from scrolling (mobile only; on desktop
    // the aside is inline and never "open", so this is a no-op there).
    if (window.matchMedia && window.matchMedia("(max-width: 899px)").matches) {
      document.documentElement.style.overflow = open ? "hidden" : "";
    }
    this.aside.classList.toggle("is-open", open);
    this.sheetToggle.setAttribute("aria-expanded", open ? "true" : "false");
    this.sheetOverlay.hidden = !open;
  }

  reset(options) {
    this.savingEnabled = !(options && options.save === false);
    this.setSheet(false);
    this.state = {
      step: this.steps[0],
      box: null,
      items: [],
      cards: [],
      loading: false
    };
    this.querySelectorAll("[data-bab-box]").forEach((el) => {
      el.classList.remove("is-selected");
      el.setAttribute("aria-pressed", "false");
    });
    if (this.messageInput) this.messageInput.value = "";
    if (this.fromInput) this.fromInput.value = "";
    if (this.skipInput) this.skipInput.checked = false;
    this.hideError();
    this.render();
    this.savingEnabled = true;
  }

  /* ---------- persistence ---------- */

  storageKey() {
    return "bab-state:" + window.location.pathname;
  }

  hasSaved() {
    try {
      return !!window.localStorage.getItem(this.storageKey());
    } catch (e) {
      return false;
    }
  }

  clearSaved() {
    try {
      window.localStorage.removeItem(this.storageKey());
    } catch (e) {}
  }

  // Only ids and text are stored; names, images, prices and stock are re-read from the
  // freshly rendered page on restore, so a returning customer never sees stale prices.
  save() {
    if (!this.savingEnabled || !this.state) return;
    const state = this.state;
    const empty = !state.box && !state.items.length && !state.cards.length;
    try {
      if (empty) {
        window.localStorage.removeItem(this.storageKey());
        return;
      }
      window.localStorage.setItem(
        this.storageKey(),
        JSON.stringify({
          ts: Date.now(),
          step: state.step,
          boxIndex: state.box ? state.box.index : null,
          items: state.items.map((item) => item.variantId),
          cards: state.cards.map((card) => card.variantId),
          message: this.messageInput ? this.messageInput.value : "",
          from: this.fromInput ? this.fromInput.value : "",
          skip: this.skipped()
        })
      );
    } catch (e) {}
  }

  restoreSaved() {
    let saved = null;
    try {
      saved = JSON.parse(window.localStorage.getItem(this.storageKey()));
    } catch (e) {}
    // Saved builds expire after a day.
    if (!saved || Date.now() - saved.ts > 24 * 60 * 60 * 1000) return;

    const boxEl = this.querySelectorAll("[data-bab-box]")[saved.boxIndex];
    // A saved box that has since sold out can't be restored.
    if (!boxEl || boxEl.dataset.available === "false") return;
    this.applyBox(boxEl, saved.boxIndex);

    (saved.items || []).forEach((variantId) => {
      const data = this.findVariant(String(variantId), "product");
      if (!data) return;
      const box = this.state.box;
      if (box.max > 0 && this.state.items.length >= box.max) return;
      data.uid = ++this.uid;
      this.state.items.push(data);
    });

    // "card" (single) is what earlier versions saved; "cards" is the current list.
    const savedCards = saved.cards || (saved.card ? [saved.card] : []);
    savedCards.forEach((variantId) => {
      const data = this.findVariant(String(variantId), "card");
      if (data) this.state.cards.push(data);
    });

    if (this.messageInput) this.messageInput.value = saved.message || "";
    if (this.fromInput) this.fromInput.value = saved.from || "";
    if (this.skipInput) this.skipInput.checked = !!saved.skip;

    if (this.steps.indexOf(saved.step) > 0) this.state.step = saved.step;
  }

  // Finds a variant among the rendered tiles (default variants and select options alike)
  // and returns fresh data for it, switching that tile's select to it so counts line up.
  findVariant(variantId, kind) {
    const tiles = this.querySelectorAll('[data-bab-product][data-kind="' + kind + '"]');
    for (const tile of tiles) {
      const select = tile.querySelector("[data-variant-select]");
      if (select) {
        const option = Array.from(select.options).find((o) => o.value === variantId);
        if (option) {
          select.value = variantId;
          this.applyVariant(tile, select);
          return this.cardData(tile);
        }
      } else if (tile.dataset.variantId === variantId) {
        return this.cardData(tile);
      }
    }
    return null;
  }

  /* ---------- helpers ---------- */

  formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: "currency",
        currency: this.currency
      }).format(amount);
    } catch (e) {
      return "$" + amount.toFixed(2);
    }
  }

  t(key, vars) {
    let text = this.strings[key] || "";
    Object.keys(vars || {}).forEach((name) => {
      text = text.replace("{{ " + name + " }}", vars[name]).replace("{{" + name + "}}", vars[name]);
    });
    return text;
  }

  total() {
    const items = this.state.items.reduce((sum, item) => sum + item.price, 0);
    const box = this.state.box ? this.state.box.price : 0;
    const cards = this.state.cards.reduce((sum, card) => sum + card.price, 0);
    return box + items + cards;
  }

  stepIndex() {
    return this.steps.indexOf(this.state.step);
  }

  skipped() {
    return !!(this.skipInput && this.skipInput.checked);
  }

  showToast(message) {
    if (!this.toastEl) return;
    const textEl = this.toastEl.querySelector("[data-toast-text]");
    if (textEl) textEl.textContent = message;
    this.toastEl.classList.add("is-visible");
    this.toastEl.setAttribute("aria-hidden", "false");
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toastEl.classList.remove("is-visible");
      this.toastEl.setAttribute("aria-hidden", "true");
    }, 1800);
  }

  showError(message) {
    this.errorEls.forEach((el) => {
      el.textContent = message;
      el.hidden = false;
    });
  }

  hideError() {
    this.errorEls.forEach((el) => {
      el.hidden = true;
    });
  }

  /* ---------- events ---------- */

  handleClick(event) {
    const boxEl = event.target.closest("[data-bab-box]");
    if (boxEl) {
      // A sold-out priced box (disabled in renderBoxes) must never be selectable.
      if (!boxEl.disabled && !boxEl.classList.contains("is-sold-out")) this.selectBox(boxEl);
      return;
    }

    const tabEl = event.target.closest("[data-tab]");
    if (tabEl) {
      this.switchTab(tabEl);
      return;
    }

    const loadMoreEl = event.target.closest("[data-load-more]");
    if (loadMoreEl) {
      this.loadMore(loadMoreEl);
      return;
    }

    const addEl = event.target.closest("[data-bab-add]");
    if (addEl && !addEl.disabled) {
      const card = addEl.closest("[data-bab-product]");
      if (card.dataset.kind === "card") this.toggleCard(card);
      else this.addItem(card);
      return;
    }

    const gotoEl = event.target.closest("[data-goto]");
    if (gotoEl) {
      this.state.step = gotoEl.dataset.goto;
      this.hideError();
      this.render();
      this.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const removeEl = event.target.closest("[data-remove-item]");
    if (removeEl) {
      const uid = Number(removeEl.dataset.removeItem);
      this.state.items = this.state.items.filter((item) => item.uid !== uid);
      this.render();
      return;
    }

    const removeCardEl = event.target.closest("[data-remove-card]");
    if (removeCardEl) {
      const variantId = removeCardEl.dataset.removeCard;
      this.state.cards = this.state.cards.filter((card) => card.variantId !== variantId);
      this.render();
    }
  }

  handleChange(event) {
    const select = event.target.closest("[data-variant-select]");
    if (select) {
      this.applyVariant(select.closest("[data-bab-product]"), select);
      this.render();
      return;
    }

    if (event.target === this.skipInput) {
      this.render();
    }
  }

  applyVariant(card, select) {
    const option = select.selectedOptions[0];
    card.dataset.variantId = select.value;
    card.dataset.price = option.dataset.price;
    card.dataset.max = option.dataset.max;
    card.dataset.tracked = option.dataset.tracked;
    card.dataset.variantTitle = option.dataset.variantTitle || "";
    card.querySelector("[data-price-display]").textContent = option.dataset.priceFormatted;
  }

  handleInput(event) {
    if (event.target === this.messageInput || event.target === this.fromInput) {
      this.render();
    }
  }

  /* ---------- actions ---------- */

  applyBox(el, index) {
    this.state.box = {
      index: index,
      name: el.dataset.name,
      image: el.dataset.image,
      max: Number(el.dataset.max) || 0,
      // Only set when the merchant assigned a (priced) box product to this block.
      variantId: el.dataset.variantId || "",
      price: el.dataset.variantId ? Number(el.dataset.price) || 0 : 0
    };
    this.querySelectorAll("[data-bab-box]").forEach((other) => {
      const active = other === el;
      other.classList.toggle("is-selected", active);
      other.setAttribute("aria-pressed", active ? "true" : "false");
    });
    // Switching to a smaller box: trim the extras off the end so capacity holds.
    if (this.state.box.max > 0 && this.state.items.length > this.state.box.max) {
      this.state.items.length = this.state.box.max;
    }
  }

  selectBox(el) {
    const previousBox = this.state.box;
    const index = Array.from(this.querySelectorAll("[data-bab-box]")).indexOf(el);
    this.applyBox(el, index);
    this.render();
    if (!previousBox) this.go(1);
  }

  // Reveals the next batch of tiles the server rendered hidden; removes the button once
  // nothing is left.
  loadMore(button) {
    const grid = button.closest("[data-tab-panel]");
    const batch = Number(button.dataset.batch) || 25;
    const hiddenTiles = Array.from(grid.querySelectorAll("[data-extra][hidden]"));
    hiddenTiles.slice(0, batch).forEach((tile) => {
      tile.hidden = false;
    });
    if (hiddenTiles.length <= batch) button.closest(".bab__load-more").remove();
    this.render();
  }

  switchTab(tabEl) {
    const panel = tabEl.closest("[data-panel]");
    panel.querySelectorAll("[data-tab]").forEach((tab) => {
      const active = tab === tabEl;
      tab.classList.toggle("is-active", active);
      tab.setAttribute("aria-selected", active ? "true" : "false");
    });
    panel.querySelectorAll("[data-tab-panel]").forEach((grid) => {
      grid.hidden = grid.dataset.tabPanel !== tabEl.dataset.tab;
    });
  }

  cardData(card) {
    const title = card.dataset.title;
    const variantTitle = card.dataset.variantTitle;
    return {
      productId: card.dataset.productId,
      variantId: card.dataset.variantId,
      title: variantTitle ? title + " — " + variantTitle : title,
      image: card.dataset.image,
      price: Number(card.dataset.price) || 0,
      max: Number(card.dataset.max),
      tracked: card.dataset.tracked === "true"
    };
  }

  addItem(card) {
    const data = this.cardData(card);
    const box = this.state.box;
    if (box && box.max > 0 && this.state.items.length >= box.max) {
      this.showToast(this.t("box_full", { count: box.max }));
      return;
    }
    const sameVariant = this.state.items.filter((item) => item.variantId === data.variantId).length;
    if (sameVariant >= this.remaining(data.variantId, data.max, data.tracked)) {
      this.showToast(this.t("max_qty"));
      return;
    }
    data.uid = ++this.uid;
    this.state.items.push(data);
    this.showToast(this.t("added"));
    this.render();
  }

  toggleCard(card) {
    const data = this.cardData(card);
    // Any number of different cards can be picked; clicking a picked card again removes it.
    if (this.state.cards.some((card) => card.variantId === data.variantId)) {
      this.state.cards = this.state.cards.filter((card) => card.variantId !== data.variantId);
    } else {
      this.state.cards.push(data);
    }
    this.render();
  }

  go(direction) {
    const next = this.stepIndex() + direction;
    if (next < 0 || next >= this.steps.length) return;
    this.state.step = this.steps[next];
    this.setSheet(false);
    this.hideError();
    this.render();
    this.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  /* Returns an error string when the current step cannot be left yet, otherwise "". */
  validateStep() {
    switch (this.state.step) {
      case "box":
        return this.state.box ? "" : this.t("needs_box");
      case "products":
        return this.state.items.length ? "" : this.t("needs_products");
      case "card":
        return this.cardRequired && !this.state.cards.length ? this.t("needs_card") : "";
      case "message": {
        if (this.skipped()) return "";
        const message = (this.messageInput.value || "").trim();
        // Customers must either write a message or tick the skip box. When the merchant
        // disables skipping, the checkbox isn't rendered, so a message is always required.
        return message ? "" : this.t("needs_message");
      }
    }
    return "";
  }

  onNext() {
    if (this.state.loading) return;
    const error = this.validateStep();
    if (error) {
      if (this.state.step === "message") this.showError(error);
      else this.showToast(error);
      return;
    }
    if (this.state.step === this.steps[this.steps.length - 1]) {
      this.addToCart();
    } else {
      this.go(1);
    }
  }

  /* ---------- render ---------- */

  render() {
    const state = this.state;
    const index = this.stepIndex();
    const last = this.steps.length - 1;
    this.dataset.step = state.step;

    Object.keys(this.panels).forEach((name) => {
      this.panels[name].hidden = name !== state.step;
    });

    this.progressSteps.forEach((el) => {
      const i = this.steps.indexOf(el.dataset.progressStep);
      el.classList.toggle("is-active", i === index);
      el.classList.toggle("is-done", i < index);
    });
    // Labels are equal-width and centred, so the active label's centre is (i + 0.5) / n.
    // The gift icon slides to it (forwards and back) under the line.
    const progress = ((index + 0.5) / this.steps.length) * 100;
    this.progressFill.style.width = progress + "%";
    this.progressMarker.style.left = progress + "%";

    this.totalEl.textContent = this.formatMoney(this.total());
    this.sheetTotalEl.textContent = this.totalEl.textContent;

    // Selected box + capacity
    if (state.box) {
      this.selectedBoxEl.hidden = false;
      if (state.box.image) this.selectedBoxImage.src = state.box.image;
      else this.selectedBoxImage.removeAttribute("src");
      this.selectedBoxImage.hidden = !state.box.image;
      this.selectedBoxName.textContent =
        state.box.name + (state.box.variantId ? " — " + this.formatMoney(state.box.price) : "");
    } else {
      this.selectedBoxEl.hidden = true;
    }
    if (state.box && state.box.max > 0) {
      this.capacityEl.hidden = false;
      this.capacityEl.textContent = state.items.length + " / " + state.box.max;
    } else {
      this.capacityEl.hidden = true;
    }

    this.renderBoxes();
    this.renderReview();
    this.renderTiles();
    this.renderCards();

    // Message counter
    if (this.messageInput && this.counterEl) {
      this.counterEl.textContent = this.messageInput.value.length + "/" + this.messageMax;
    }
    if (this.messageFields) this.messageFields.hidden = this.skipped();

    // Nav
    this.prevButton.hidden = index === 0;
    this.nextButton.textContent = index === last ? this.t("add_to_cart") : this.t("next");
    this.nextButton.disabled = state.loading;

    this.save();
  }

  renderReview() {
    if (this.state.step !== "review") return;
    const state = this.state;

    const addRow = (list, entry, quantity, price) => {
      const li = document.createElement("li");
      li.className = "bab-review__row";
      if (entry.image) {
        const img = document.createElement("img");
        img.src = entry.image;
        img.alt = "";
        img.width = 64;
        img.height = 64;
        li.appendChild(img);
      }
      const name = document.createElement("span");
      name.className = "bab-review__name fs-body-100";
      name.textContent = entry.title + (quantity > 1 ? " × " + quantity : "");
      li.appendChild(name);
      const amount = document.createElement("span");
      amount.className = "bab-review__price fs-body-100 ff-product-price";
      amount.textContent = price === null ? "" : this.formatMoney(price);
      li.appendChild(amount);
      list.appendChild(li);
    };

    this.reviewBox.innerHTML = "";
    if (state.box) {
      addRow(
        this.reviewBox,
        { title: state.box.name, image: state.box.image },
        1,
        state.box.variantId ? state.box.price : null
      );
    }

    // Same variant picked more than once shows as one row with a quantity.
    this.reviewItems.innerHTML = "";
    const grouped = [];
    state.items.forEach((item) => {
      const existing = grouped.find((row) => row.item.variantId === item.variantId);
      if (existing) existing.quantity += 1;
      else grouped.push({ item: item, quantity: 1 });
    });
    grouped.forEach((row) => addRow(this.reviewItems, row.item, row.quantity, row.item.price * row.quantity));

    if (this.reviewCard) {
      this.reviewCard.innerHTML = "";
      state.cards.forEach((card) => addRow(this.reviewCard, card, 1, card.price));
      // No card picked (it was optional): leave the whole section out of the review.
      this.reviewCard.closest(".bab-review__section").hidden = !state.cards.length;
    }

    const message = this.skipped() ? "" : (this.messageInput.value || "").trim();
    const from = this.skipped() ? "" : (this.fromInput.value || "").trim();
    this.reviewNote.innerHTML = "";
    if (message || from) {
      if (message) {
        const p = document.createElement("p");
        p.textContent = message;
        this.reviewNote.appendChild(p);
      }
      if (from) {
        const p = document.createElement("p");
        p.textContent = this.t("from") + ": " + from;
        this.reviewNote.appendChild(p);
      }
    } else {
      this.reviewNote.textContent = this.t("no_message");
    }

    this.reviewTotal.textContent = this.formatMoney(this.total());
  }

  // A priced box product that is unavailable, or whose remaining stock is used up by
  // what's already in the cart, can't be picked.
  renderBoxes() {
    this.querySelectorAll("[data-bab-box]").forEach((el) => {
      if (!el.dataset.variantId) return;
      const remaining = this.remaining(el.dataset.variantId, Number(el.dataset.stock), el.dataset.tracked === "true");
      const soldOut = el.dataset.available === "false" || remaining <= 0;
      el.disabled = soldOut;
      el.classList.toggle("is-sold-out", soldOut);
      const label = el.querySelector("[data-box-sold]");
      if (label) label.hidden = !soldOut;
    });
  }

  renderTiles() {
    const { items, cards } = this.state;
    this.tilesEl.innerHTML = "";
    this.boxEmptyEl.hidden = items.length > 0 || cards.length > 0;

    const makeTile = (entry, removeAttr, removeValue, isCard) => {
      const li = document.createElement("li");
      li.className = "bab-tile" + (isCard ? " bab-tile--card" : "");

      if (entry.image) {
        const img = document.createElement("img");
        img.src = entry.image;
        img.alt = entry.title;
        img.width = 240;
        img.height = 240;
        li.appendChild(img);
      }

      const label = document.createElement("span");
      label.className = "bab-tile__label fs-body-75";
      label.textContent = (isCard ? this.t("card") + ": " : "") + entry.title;
      li.appendChild(label);

      const button = document.createElement("button");
      button.type = "button";
      button.className = "bab-tile__remove";
      button.setAttribute(removeAttr, removeValue);
      button.setAttribute("aria-label", this.t("remove") + " " + entry.title);
      button.innerHTML =
        '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8"/></svg>';
      li.appendChild(button);
      return li;
    };

    items.forEach((item) => this.tilesEl.appendChild(makeTile(item, "data-remove-item", String(item.uid), false)));
    cards.forEach((card) => this.tilesEl.appendChild(makeTile(card, "data-remove-card", card.variantId, true)));
  }

  renderCards() {
    const counts = {};
    this.state.items.forEach((item) => {
      counts[item.variantId] = (counts[item.variantId] || 0) + 1;
    });
    const box = this.state.box;
    const boxFull = !!(box && box.max > 0 && this.state.items.length >= box.max);

    this.querySelectorAll("[data-bab-product]").forEach((card) => {
      const kind = card.dataset.kind;
      const button = card.querySelector("[data-bab-add]");
      const remaining = this.remaining(
        card.dataset.variantId,
        Number(card.dataset.max),
        card.dataset.tracked === "true"
      );
      const soldOut = card.dataset.available === "false" || remaining <= 0;
      card.classList.toggle("is-sold-out", soldOut);
      const soldBadge = card.querySelector("[data-sold-out-badge]");
      if (soldBadge) soldBadge.hidden = !soldOut;
      // Sold-out items keep a disabled button that reads "Sold out" instead of "Add".
      const labelDefault = button.querySelector("[data-label-default]");
      const labelActive = button.querySelector("[data-label-active]");
      const labelSold = button.querySelector("[data-label-sold]");
      const showSold = (active) => {
        if (labelSold) labelSold.hidden = !soldOut;
        if (labelDefault) labelDefault.hidden = soldOut || active;
        if (labelActive) labelActive.hidden = soldOut || !active;
      };

      if (kind === "card") {
        const active = this.state.cards.some((picked) => picked.variantId === card.dataset.variantId);
        card.classList.toggle("is-selected", active);
        showSold(active);
        // A selected card stays clickable so it can be deselected.
        button.disabled = soldOut && !active;
        return;
      }
      const count = counts[card.dataset.variantId] || 0;
      showSold(false);
      const badge = card.querySelector("[data-count]");
      badge.hidden = count === 0;
      badge.textContent = "×" + count;
      card.classList.toggle("is-selected", count > 0);
      button.disabled = soldOut || boxFull || count >= remaining;
    });
  }

  /* ---------- cart ---------- */

  // Number for the next box: one more than the highest "Box N" label already in the cart,
  // so removing Box 1 and building another never produces two boxes labelled the same.
  nextBoxNumber(cart) {
    let highest = 0;
    ((cart && cart.items) || []).forEach((line) => {
      const match = /^Box (\d+)$/.exec((line.properties && line.properties.Box) || "");
      if (match) highest = Math.max(highest, Number(match[1]));
    });
    return highest + 1;
  }

  buildLines(boxLabel) {
    const state = this.state;
    const boxId = "box_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8);
    const skipped = this.skipped();
    const message = skipped ? "" : (this.messageInput.value || "").trim();
    const from = skipped ? "" : (this.fromInput.value || "").trim();

    // "_"-prefixed properties are hidden from customers and drive the cart grouping;
    // "Box" is the visible label so each line reads "Box: Box 1" at checkout.
    const baseProperties = { _box_id: boxId, _box_name: state.box.name, Box: boxLabel };
    // Lets the cart show the chosen box artwork at the top of the grouped block.
    if (state.box.image) baseProperties._box_image = state.box.image;
    if (message) baseProperties._box_message = message;
    if (from) baseProperties._box_from = from;

    // Same variant picked more than once collapses into one line with a quantity.
    const lines = [];
    // The optional priced box comes first so it heads the group in the cart.
    if (state.box.variantId) {
      // "_box_role" marks the box product's own line so the cart can fold it into the
      // block's header instead of listing it as one of the items.
      lines.push({
        id: state.box.variantId,
        quantity: 1,
        properties: Object.assign({}, baseProperties, { _box_role: "box" })
      });
    }
    state.items.forEach((item) => {
      const existing = lines.find((line) => line.id === item.variantId);
      if (existing) existing.quantity += 1;
      else lines.push({ id: item.variantId, quantity: 1, properties: Object.assign({}, baseProperties) });
    });
    state.cards.forEach((card) => {
      lines.push({ id: card.variantId, quantity: 1, properties: Object.assign({}, baseProperties) });
    });

    // Customer-visible copy of the note on the first line only (the box line when a box
    // product is assigned), so it shows at checkout and on the packing slip without
    // repeating on every item.
    if (message) lines[0].properties["Gift Message"] = message;
    if (from) lines[0].properties["From"] = from;
    return lines;
  }

  addToCart() {
    const state = this.state;

    state.loading = true;
    this.nextButton.textContent = this.t("adding");
    this.nextButton.disabled = true;
    this.hideError();

    fetch("/cart.js", { credentials: "include" })
      .then((response) => response.json())
      .catch(() => ({ items: [] }))
      .then((cart) => {
        const lines = this.buildLines("Box " + this.nextBoxNumber(cart));
        return fetch("/cart/add.js", {
          method: "POST",
          credentials: "include",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ items: lines })
        });
      })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data: data })))
      .then(({ ok, data }) => {
        if (!ok || data.status) {
          throw new Error((data && (data.description || data.message)) || this.t("generic_error"));
        }
        // Submitted: forget the saved build so coming back starts fresh.
        this.savingEnabled = false;
        this.clearSaved();
        window.location.href = "/cart";
      })
      .catch((error) => {
        state.loading = false;
        // Stock may have changed since the page loaded; resync so sold-out items disable.
        this.refreshCartQty();
        this.render();
        this.showError(error.message || this.t("generic_error"));
      });
  }

  refreshCartChrome() {
    if (typeof window.emit !== "function") return;
    if (BuildABoxApp._refreshing) return;
    BuildABoxApp._refreshing = fetch("/cart.js", { credentials: "include" })
      .then((response) => response.json())
      .then((cart) => {
        window.emit("cart:updated", { cart: cart });
        window.emit("quick-cart:updated");
      })
      .catch(() => {})
      .then(() => {
        BuildABoxApp._refreshing = null;
      });
  }
}

customElements.define("build-a-box-app", BuildABoxApp);
